/* The minimap: the whole path at 186×116, with the current viewport drawn on
   top. It is a real target — dragging it pans the map. */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NODES } from '../../data/nodes';
import { MINI, miniScale, WORLD, type View } from '../../engine/graph';
import { progress as chapterProgress, stateOf } from '../../engine/progress';
import { useApp } from '../../state/AppProvider';

interface MinimapProps {
  view: View;
  /** size of the canvas this minimap describes */
  size: { w: number; h: number };
  onPan: (worldX: number, worldY: number) => void;
}

export function Minimap({ view, size, onPan }: MinimapProps) {
  const { t } = useTranslation();
  const { progress, sel } = useApp();
  const boxRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const scale = miniScale();

  const panTo = (clientX: number, clientY: number) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    onPan((clientX - box.left - MINI.pad) / scale, (clientY - box.top - MINI.pad) / scale);
  };

  return (
    <div
      className="minimap"
      id="minimap"
      data-od-id="minimap"
      role="img"
      aria-label={t('minimap')}
      ref={boxRef}
      onPointerDown={(event) => {
        dragging.current = true;
        boxRef.current?.setPointerCapture(event.pointerId);
        panTo(event.clientX, event.clientY);
      }}
      onPointerMove={(event) => {
        if (dragging.current) panTo(event.clientX, event.clientY);
      }}
      onPointerUp={(event) => {
        dragging.current = false;
        try {
          boxRef.current?.releasePointerCapture(event.pointerId);
        } catch {
          /* the pointer may already be gone */
        }
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
    >
      <svg id="mini-svg" viewBox={`0 0 ${MINI.w} ${MINI.h}`} aria-hidden="true">
        {NODES.map((node) => {
          const mx = MINI.pad + node.x * scale;
          const my = MINI.pad + node.y * scale;
          const mw = Math.max(3, node.w * scale);
          const mh = Math.max(2.5, node.h * scale);
          const ratio = chapterProgress(node, progress) / node.blocks.length;
          return (
            <g key={node.id}>
              <rect
                className="mn"
                data-state={sel === node.id ? 'active' : stateOf(node, progress)}
                x={mx}
                y={my}
                width={mw}
                height={mh}
                rx={1.5}
              />
              {ratio > 0 && <rect className="mb" x={mx} y={my + mh + 0.7} width={mw * ratio} height={1.4} rx={0.7} />}
            </g>
          );
        })}
        {/* The visible region, clamped to the world: when the fitted world is
            smaller than the canvas (a height-limited fit), the raw
            `size / k` is wider than the world itself and the rect drew
            outside the minimap. */}
        {(() => {
          const wx = Math.max(0, Math.min(WORLD.w, -view.x / view.k));
          const wy = Math.max(0, Math.min(WORLD.h, -view.y / view.k));
          const ww = Math.max(4, Math.min(WORLD.w - wx, size.w / view.k));
          const wh = Math.max(4, Math.min(WORLD.h - wy, size.h / view.k));
          return (
            <rect
              className="vp"
              x={MINI.pad + wx * scale}
              y={MINI.pad + wy * scale}
              width={ww * scale}
              height={wh * scale}
              rx={2}
            />
          );
        })()}
      </svg>
    </div>
  );
}
